import React, { useState } from "react";
import { Link } from 'react-router-dom';
import styles from "./Home.module.css";
import {
  FaTools,
  FaCheckCircle,
  FaPhoneAlt,
  FaArrowRight,
  FaChartBar,
  FaHandshake,
  FaDesktop,
  FaNetworkWired,
  FaMapMarkerAlt,
  FaComments,
  FaHeadset,
  FaArrowDown
} from "react-icons/fa";
import ContactForm from "../components/ContactForm";

function Home() {
  const [showContactForm] = useState(true);

  const supportPlans = [
    {
      name: "Basic",
      audience: "Solo operators and 1 to 3 person shops",
      price: "$99",
      period: "/mo",
      note: "A simple starting point for priority help and light monthly support.",
      benefits: ["60 minutes remote support each month", "Priority scheduling", "$75/hr discounted support rate"],
      link: "/contact-us?plan=basic",
      cta: "Ask about Basic",
    },
    {
      name: "Standard",
      audience: "Small teams with daily technology needs",
      price: "$149",
      period: "/mo",
      note: "The best fit for most small businesses that want predictable support.",
      benefits: ["90 minutes remote support each month", "Priority scheduling", "Quarterly system health check"],
      link: "/contact-us?plan=standard",
      cta: "Ask about Standard",
      featured: true,
    },
    {
      name: "Premium",
      audience: "Growing businesses or multiple locations",
      price: "$199",
      period: "/mo",
      note: "Extra coverage when technology is more central to daily operations.",
      benefits: ["120 minutes remote support each month", "Priority scheduling", "On-site hour or added health checks"],
      link: "/contact-us?plan=premium",
      cta: "Ask about Premium",
    },
  ];


  return (
    <main className={styles.homeContainer}>
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <div className={styles.heroLocalTag}>
            <FaMapMarkerAlt /> Local IT Support for Shreveport-Bossier Small Businesses
          </div>
          <h1 className={styles.heroHeading}>
            Local IT help for when you need things to Just Work.
          </h1>
          <p className={styles.heroBody}>
            Work directly with Austin for practical help with computers, networks, email, backups, security, and everyday tech problems.
          </p>

          <div className={styles.heroCTAs}>
            <Link to="/contact-us?source=hero" className={`btn btn-primary ${styles.ctaButton}`}>
              Talk to Austin
            </Link>
            <Link to="/it-health-check" className={`${styles.ctaButton} ${styles.secondaryCTAButton}`}>
              Start with a Health Check
            </Link>
          </div>

          <div className={styles.heroTrustLine} aria-label="Trust cues">
            <span>Local</span>
            <span>Plain-English</span>
            <span>Work directly with Austin</span>
            <span>No ticket maze</span>
          </div>
        </div>
        <a href="#problems" className={styles.scrollCue} aria-label="Scroll to common IT problems">
          <FaArrowDown />
        </a>
      </section>


      <section className={styles.intentSection} id="problems">
        <div className={styles.container}>
          <div className={styles.sectionIntro}>
            <span className={styles.sectionEyebrow}>Start with the problem</span>
            <h2 className={styles.sectionTitle}>Tell Austin what is getting in the way</h2>
            <p className={styles.sectionSubtitle}>
              No need to diagnose it first. Start with what is broken, what needs setting up, or what you want to stop worrying about.
            </p>
          </div>

          <div className={styles.intentGrid}>
            <Link to="/computer-repair" className={styles.intentCard}>
              <div className={styles.intentIcon}><FaTools /></div>
              <h3>Something's broken</h3>
              <p>Slow computers, Wi‑Fi, printers, email, and office tech problems that stop work.</p>
              <span className={styles.intentLink}>Get repair help <FaArrowRight /></span>
            </Link>

            <Link to="/it-consulting" className={styles.intentCard}>
              <div className={styles.intentIcon}><FaNetworkWired /></div>
              <h3>Need it set up right</h3>
              <p>Computers, networks, backups, cloud tools, security basics, and practical setup guidance.</p>
              <span className={styles.intentLink}>Plan the setup <FaArrowRight /></span>
            </Link>

            <Link to="/managed-it-services" className={styles.intentCard}>
              <div className={styles.intentIcon}><FaHeadset /></div>
              <h3>Want fewer tech surprises</h3>
              <p>Monthly support, maintenance, backup checks, and clear guidance before issues pile up.</p>
              <span className={styles.intentLink}>See ongoing support <FaArrowRight /></span>
            </Link>
          </div>

          <div className={styles.intentFooterCta}>
            <Link to="/contact-us?source=not-sure" className={`btn btn-primary ${styles.ctaButton}`}>
              Not sure? Ask Austin
            </Link>
          </div>
        </div>
      </section>

      <section className={styles.processSection} id="process">
        <div className={styles.container}>
          <div className={styles.sectionIntro}>
            <span className={styles.sectionEyebrow}>How it works</span>
            <h2 className={styles.sectionTitle}>A straightforward path from problem to next step</h2>
            <p className={styles.sectionSubtitle}>Simple, practical, and focused on keeping work moving.</p>
          </div>

          <div className={styles.processSteps}>
            <div className={styles.processStep}>
              <div className={styles.processStepNumber}>1</div>
              <div className={styles.processStepContent}>
                <div className={styles.processStepIcon}><FaComments /></div>
                <h3>Talk it through</h3>
                <p>Share what is going wrong and what outcome matters most.</p>
              </div>
            </div>
            <div className={styles.processStep}>
              <div className={styles.processStepNumber}>2</div>
              <div className={styles.processStepContent}>
                <div className={styles.processStepIcon}><FaChartBar /></div>
                <h3>Get a second pair of eyes</h3>
                <p>Austin looks over the systems, risks, and practical constraints with you.</p>
              </div>
            </div>
            <div className={styles.processStep}>
              <div className={styles.processStepNumber}>3</div>
              <div className={styles.processStepContent}>
                <div className={styles.processStepIcon}><FaCheckCircle /></div>
                <h3>Get clear next steps</h3>
                <p>You get plain-English recommendations and options.</p>
              </div>
            </div>
            <div className={styles.processStep}>
              <div className={styles.processStepNumber}>4</div>
              <div className={styles.processStepContent}>
                <div className={styles.processStepIcon}><FaHandshake /></div>
                <h3>A plan for peace of mind</h3>
                <p>Move into the right repair, setup project, or ongoing support plan without guesswork.</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section className={styles.starterOfferSection} id="starter-offer">
        <div className={styles.container}>
          <div className={styles.starterOfferCard}>
            <div>
              <span className={styles.sectionEyebrow}>Simple first step</span>
              <h2>Start with a $149 IT Health Check</h2>
              <p>
                Austin reviews your computers, network, backups, security basics, and current pain points, then gives you plain-English next steps before you commit to bigger work.
              </p>
            </div>
            <Link to="/it-health-check" className={`btn btn-primary ${styles.ctaButton}`}>
              Start the Health Check
            </Link>
          </div>
        </div>
      </section>

      <section className={styles.contactSection} id="contact">
        <div className={styles.container}>
          <div className={styles.sectionIntro}>
            <span className={styles.sectionEyebrow}>Get in touch</span>
            <h2 className={styles.sectionTitle}>Ready to talk through your IT needs?</h2>
            <p className={styles.sectionSubtitle}>
              Share what is going on and Austin will respond with the next practical step.
            </p>
          </div>

          <div className={styles.contactReassuranceRow}>
            <div className={styles.contactReassuranceItem}><FaComments /> Talk directly with Austin</div>
            <div className={styles.contactReassuranceItem}><FaDesktop /> On-site and remote support available</div>
            <div className={styles.contactReassuranceItem}><FaCheckCircle /> Clear next steps</div>
          </div>

          <div className={styles.contactFormWrapper}>
            {showContactForm && <ContactForm leadSource="homepage-cta" />}
          </div>

          <div className={styles.contactAltCTAContainer}>
            <p className={styles.contactAltText}>Prefer to call instead?</p>
            <a href="tel:3186587801" className={`btn btn-primary ${styles.ctaButton}`}>
              <FaPhoneAlt /> (318) 658-7801
            </a>
          </div>
        </div>
      </section>

      <section className={styles.pricingSection} id="support-plans">
        <div className={styles.container}>
          <div className={styles.sectionIntro}>
            <span className={`${styles.sectionEyebrow} ${styles.darkEyebrow}`}>Monthly support plans</span>
            <h2 className={styles.sectionTitle}>If monthly support makes sense, keep it simple</h2>
            <p className={styles.sectionSubtitle}>The Health Check is the best first step. These clean plan cards give you a quick comparison after you know what your setup needs.</p>
          </div>

          <div className={styles.pricingGrid}>
            {supportPlans.map((plan) => (
              <article key={plan.name} className={`${styles.pricingCard} ${plan.featured ? styles.pricingCardFeatured : ""}`}>
                {plan.featured && <div className={styles.pricingBadge}>Recommended</div>}
                <div className={styles.pricingHeader}>
                  <h3>{plan.name}</h3>
                  <p>{plan.audience}</p>
                </div>
                <div className={styles.pricingPrice}>
                  <span>{plan.price}</span>
                  <small>{plan.period}</small>
                </div>
                <p className={styles.pricingNote}>{plan.note}</p>
                <ul className={styles.pricingBenefits}>
                  {plan.benefits.map((benefit) => (
                    <li key={benefit}><FaCheckCircle /> {benefit}</li>
                  ))}
                </ul>
                <Link to={plan.link} className={`${plan.featured ? "btn btn-primary" : styles.pricingCTASecondary} ${styles.pricingCTA}`}>
                  {plan.cta}
                </Link>
              </article>
            ))}
          </div>

          <div className={styles.pricingTrustLine}>
            <FaCheckCircle /> Plans stay practical, month-to-month, and sized around what your business actually needs.
          </div>
        </div>
      </section>

      <section className={styles.specificNeedSection} id="resources">
        <div className={styles.container}>
          <div className={styles.specificNeedStrip}>
            <div>
              <span className={styles.sectionEyebrow}>Need something specific?</span>
              <h2>Find the right next step without digging around.</h2>
              <p>Use the hubs for service details, local support options, and practical IT guides.</p>
            </div>
            <div className={styles.specificNeedLinks} aria-label="Helpful starting points">
              <Link to="/services">View all services</Link>
              <Link to="/managed-it-services-shreveport">Local IT support options</Link>
              <Link to="/how-to-choose-it-support">Read IT guides</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
